import {
  GuidanceFileType,
  SessionInferenceMode,
} from "@/components/graphql/schema.graphql";
import { InferenceStyleFragment } from "@/components/inference/queries.graphql";
import {
  formatSessionApiJsonField,
  getWeightFromPercentage,
  isSessionBatchableMode,
} from "@/components/inference-forms/utils/utils";
import { ElementsFieldValue } from "@/components/InferenceFormComposer/FieldDefs/fieldDefs/Elements";
import { FieldValueTypes } from "@/components/InferenceFormComposer/FieldDefs/FieldValueTypes";
import { referenceMetadata } from "@/components/ui-v2/References/constants";
import {
  ReferenceMeshType,
  ReferenceType,
} from "@/components/ui-v2/References/types";
import {
  formatReferenceForAPI,
  formatReferenceMeshForAPI,
} from "@/components/ui-v2/References/utils";
import { DEFAULT_INFERENCE_FORM } from "@/features/forge/constants";
import {
  FileReferenceType,
  GuidanceFileParameters,
  InferenceParameters,
  SessionStateInferenceParameters,
} from "@/types";

const convertElementsToFiles = (
  elements: ElementsFieldValue,
): GuidanceFileParameters[] =>
  elements.flatMap((el, index) => {
    const group = index.toString();
    const files: GuidanceFileParameters[] = [];
    if (el.frontalImage) {
      files.push({
        ...convertFileReferenceToGuidanceFileParameters(
          el.frontalImage,
          "ELEMENT_FRONTAL_IMAGE",
        ),
        group,
      });
    }
    el.referenceImages?.forEach((ref) => {
      files.push({
        ...convertFileReferenceToGuidanceFileParameters(
          ref,
          "ELEMENT_REFERENCE_IMAGE",
        ),
        group,
      });
    });
    if (el.video) {
      files.push({
        ...convertFileReferenceToGuidanceFileParameters(
          el.video,
          "ELEMENT_VIDEO",
        ),
        group,
      });
    }
    return files;
  });

type StaticParametersGetter = (
  style?: InferenceStyleFragment,
) => Partial<InferenceParameters>;

/** Per-mode static parameters not set in the form but required for inference creation. */
const MODE_STATIC_PARAMETERS: Partial<
  Record<SessionInferenceMode, StaticParametersGetter>
> = {
  PROMPT_EDIT_IMAGE: (style) => ({
    ...(style?.capabilities?.inferenceStepsRange?.[0] != null && {
      numInferenceSteps: style.capabilities.inferenceStepsRange[0],
    }),
  }),
  REMOVE_BACKGROUND: () => ({ removeBackground: true, styles: [] }),
  VECTORIZE_IMAGE: () => ({ vectorize: true, styles: [] }),
  REFRAME_IMAGE: () => ({
    reframe: true,
    refill: true,
    maskForTransparency: true,
    maskBlendRadius: -1,
    guidanceScale: 30,
  }),
  REFRAME_VIDEO: () => ({
    reframe: true,
  }),
  REMOVE_BACKGROUND_VIDEO: () => ({
    removeBackground: true,
  }),
  ADD_SFX_VIDEO: () => ({
    addSfxToVideo: true,
  }),
  RETEXTURE_MESH: () => ({
    retexture: true,
  }),
  REMESH: () => ({
    remesh: true,
  }),
  RIG_MESH: () => ({
    rig: true,
  }),
  REALTIME: () => ({
    realtime: true,
  }),
};

const convertFileReferenceToGuidanceFileParameters = (
  file: Pick<FileReferenceType, "src" | "driveFileId" | "transform">,
  type: GuidanceFileType,
): GuidanceFileParameters => {
  return {
    fileId: file.driveFileId,
    type,
    url: file.src,
    preprocess: Boolean(referenceMetadata(type)?.preprocess),
    transform: file.transform,
    weight: 1,
  };
};

const MODE_GUIDANCE_FILE_TYPE: Partial<
  Record<SessionInferenceMode, GuidanceFileType>
> = {
  PROMPT_EDIT_IMAGE: "REFERENCE_IMAGE",
  PROMPT_EDIT_VIDEO: "REFERENCE_IMAGE",
  CREATE_VIDEO: "REFERENCE_IMAGE",
  RETEXTURE_MESH: "TEXTURE_IMAGE",
  CREATE_3_D: "INIT_IMAGE",
};

const convertFormValuesToCommonAPIParameters = (
  formValues: Partial<FieldValueTypes>,
  mode?: SessionInferenceMode,
) => {
  // Only batchable generation modes may produce multiple outputs. Editor/refinement
  // modes are one-in-one-out, so ignore any batchSize carried over from the source
  // asset's parameters and pin it to 1.
  const batchSize = isSessionBatchableMode(mode)
    ? formValues.batchSize ?? 1
    : 1;
  const parameters: InferenceParameters = {
    ...DEFAULT_INFERENCE_FORM.parameters,
    prompt: formValues.prompt?.prompt,
    batchSize,
    ...(formValues.dimensions === null
      ? { width: undefined, height: undefined }
      : formValues.dimensions
        ? {
            width: formValues.dimensions[0],
            height: formValues.dimensions[1],
          }
        : {}),
  };

  if (formValues.modelBlends?.length) {
    parameters.styles = formValues.modelBlends.map((b) => ({
      id: b.id,
      weight: getWeightFromPercentage(b.weight) ?? 1,
    }));
  } else if (formValues.model) {
    // The `modelWeight` field (when present in the form) is the user-facing
    // weight slider — prefer it over the weight on the model selector.
    const weightPercentage = formValues.modelWeight ?? formValues.model.weight;
    parameters.styles = [
      {
        id: formValues.model.id,
        weight: getWeightFromPercentage(weightPercentage) ?? 1,
      },
    ];
  }

  if (formValues.guidanceScale !== undefined) {
    parameters.guidanceScale = formValues.guidanceScale ?? undefined;
  }

  if (formValues.seed !== undefined) {
    parameters.seed = formValues.seed ?? undefined;
  }

  if (formValues.prompt?.promptLanguage !== undefined) {
    parameters.promptLanguage = formValues.prompt?.promptLanguage;
  }

  if (formValues.prompt?.negativePrompt != null) {
    parameters.negativePrompt = formValues.prompt.negativePrompt;
  }

  if (formValues.texture !== undefined) {
    parameters.includeTextures = formValues.texture;
  }
  if (formValues.poseMode) {
    parameters.poseMode = formValues.poseMode;
  }
  if (formValues.lowPoly !== undefined) {
    parameters.lowPoly = formValues.lowPoly;
  }
  if (formValues.generateParts !== undefined) {
    parameters.generateParts = formValues.generateParts;
  }
  if (formValues.pbrMaterials !== undefined) {
    parameters.pbrMaterials = formValues.pbrMaterials;
  }
  if (formValues.enableRigging !== undefined) {
    parameters.enableRigging = formValues.enableRigging;
  }
  if (formValues.enableAnimation !== undefined) {
    parameters.enableAnimation = formValues.enableAnimation;
  }
  if (formValues.quadMesh !== undefined) {
    parameters.quadMesh = formValues.quadMesh;
  }
  if (formValues.faceLimit !== undefined) {
    parameters.faceLimit = formValues.quadMesh
      ? Math.round(formValues.faceLimit / 2)
      : formValues.faceLimit;
  }

  const modeGuidanceFileType = MODE_GUIDANCE_FILE_TYPE[mode!];

  if (modeGuidanceFileType && formValues.guidanceFiles?.length) {
    parameters.files = formValues.guidanceFiles.map((guidanceFile) =>
      convertFileReferenceToGuidanceFileParameters(
        guidanceFile,
        modeGuidanceFileType,
      ),
    );
  }

  if (formValues.scaleFactor !== undefined) {
    parameters.upscaleRatio = formValues.scaleFactor;
  }

  if (formValues.scaleFactorVideo !== undefined) {
    parameters.upscaleRatio = formValues.scaleFactorVideo;
  }

  if (formValues.targetFps !== undefined) {
    parameters.targetFps = formValues.targetFps;
  }

  if (formValues.upscaleCreativity !== undefined) {
    parameters.creativity = getWeightFromPercentage(
      formValues.upscaleCreativity,
    );
  }

  if (formValues.creativity !== undefined) {
    parameters.creativity = getWeightFromPercentage(formValues.creativity);
  }

  if (formValues.upscaleResemblance !== undefined) {
    parameters.resemblance = getWeightFromPercentage(
      formValues.upscaleResemblance,
    );
  }

  if (formValues.inferenceSteps !== undefined) {
    parameters.numInferenceSteps =
      formValues.inferenceSteps ?? parameters.numInferenceSteps;
  }

  if (formValues.stability !== undefined) {
    parameters.stability = formValues.stability / 100;
  }
  if (formValues.similarityBoost !== undefined) {
    parameters.similarityBoost = formValues.similarityBoost / 100;
  }
  if (formValues.speakerBoost !== undefined) {
    parameters.useSpeakerBoost = formValues.speakerBoost;
  }
  if (formValues.styleExaggeration !== undefined) {
    parameters.styleExaggeration = formValues.styleExaggeration / 100;
  }
  if (formValues.audioSpeed !== undefined) {
    parameters.speed = formValues.audioSpeed;
  }
  if (formValues.promptStrength !== undefined) {
    parameters.promptStrength = formValues.promptStrength / 100;
  }
  if (formValues.audioDuration !== undefined) {
    if (formValues.audioDuration?.isAuto) {
      parameters.durationSeconds = null;
    } else if (formValues.audioDuration?.value !== undefined) {
      parameters.durationSeconds = formValues.audioDuration.value;
    }
  }

  if (formValues.multiPrompt?.length) {
    parameters.multiPrompt = formValues.multiPrompt.map((entry) => ({
      prompt: entry.prompt,
      ...(entry.promptJson ? { promptJson: entry.promptJson } : {}),
      duration: entry.duration,
    }));
    parameters.prompt = "";
    parameters.promptJson = undefined;
  }

  if (formValues.videoDuration !== undefined) {
    parameters.durationSeconds = formValues.videoDuration;
  }

  if (formValues.fps !== undefined) {
    parameters.fps = formValues.fps;
  }

  if (formValues.transparency !== undefined) {
    parameters.transparency = formValues.transparency;
  }

  if (formValues.tileability !== undefined) {
    parameters.tileability = formValues.tileability;
  }

  if (formValues.performance != null) {
    parameters.quality = formValues.performance;
  }

  if (formValues.baseShift !== undefined) {
    parameters.baseShift = formValues.baseShift;
  }

  if (formValues.maxShift !== undefined) {
    parameters.maxShift = formValues.maxShift;
  }

  if (formValues.generateAudio !== undefined) {
    parameters.generateAudio = formValues.generateAudio;
  }

  if (formValues.keepAudio !== undefined) {
    parameters.keepAudio = formValues.keepAudio;
  }

  if (formValues.videoEffects !== undefined) {
    parameters.videoEffects = formValues.videoEffects;
  }

  if (formValues.firstFrame) {
    parameters.files = [
      ...(parameters.files ?? []),
      convertFileReferenceToGuidanceFileParameters(
        formValues.firstFrame,
        "FIRST_FRAME",
      ),
    ];
  }

  if (formValues.lastFrame) {
    parameters.files = [
      ...(parameters.files ?? []),
      convertFileReferenceToGuidanceFileParameters(
        formValues.lastFrame,
        "LAST_FRAME",
      ),
    ];
  }

  if (formValues.audioTrack) {
    parameters.files = [
      ...(parameters.files ?? []),
      convertFileReferenceToGuidanceFileParameters(
        formValues.audioTrack,
        "REFERENCE_AUDIO",
      ),
    ];
  }

  if (formValues.elements?.length) {
    parameters.files = [
      ...(parameters.files ?? []),
      ...convertElementsToFiles(formValues.elements),
    ];
  }

  return parameters;
};

interface ConvertFormValuesToSessionInferenceParametersOptions {
  formValues: Partial<FieldValueTypes>;
  mode: SessionInferenceMode;
}

export const convertFormValuesToSessionInferenceParameters = (
  options: ConvertFormValuesToSessionInferenceParametersOptions,
): SessionStateInferenceParameters => {
  const { formValues, mode } = options;
  const parameters: SessionStateInferenceParameters =
    convertFormValuesToCommonAPIParameters(formValues, mode);

  if (formValues.prompt?.promptJson) {
    parameters.promptJson = formatSessionApiJsonField(
      formValues.prompt?.promptJson,
    );
  }

  if (formValues.prompt?.negativePromptJson) {
    parameters.negativePromptJson = formatSessionApiJsonField(
      formValues.prompt.negativePromptJson,
    );
  }

  if (formValues.audioDuration !== undefined) {
    parameters.autoDuration = formValues.audioDuration.isAuto;
  }

  if (formValues.realtimeMode !== undefined) {
    parameters.realtimeMode = formValues.realtimeMode;
  }

  if (formValues.sourceImages?.length) {
    parameters.files = [
      ...formValues.sourceImages.map((image) =>
        convertFileReferenceToGuidanceFileParameters(image, "INIT_IMAGE"),
      ),
      ...(parameters.files ?? []),
    ];
  }

  if (formValues.sourceVideos?.length) {
    parameters.files = [
      ...formValues.sourceVideos.map((video) =>
        convertFileReferenceToGuidanceFileParameters(video, "INIT_VIDEO"),
      ),
      ...(parameters.files ?? []),
    ];
  }

  if (formValues.referenceVideos?.length) {
    parameters.files = [
      ...formValues.referenceVideos.map((video) =>
        convertFileReferenceToGuidanceFileParameters(video, "REFERENCE_VIDEO"),
      ),
      ...(parameters.files ?? []),
    ];
  }

  if (formValues.sourceMeshes?.length) {
    parameters.files = [
      ...formValues.sourceMeshes.map((mesh) => formatReferenceMeshForAPI(mesh)),
      ...(parameters.files ?? []),
    ];
  }

  if (formValues.references?.length) {
    const referenceFiles = formValues.references
      .filter(
        (
          r,
        ): r is ReferenceType & {
          image: NonNullable<ReferenceType["image"]>;
        } => r.image !== null,
      )
      .map(formatReferenceForAPI);
    parameters.files = [...referenceFiles, ...(parameters.files ?? [])];
  }

  return parameters;
};

interface ConvertFormValuesToInferenceCreationParametersOptions {
  formValues: Partial<FieldValueTypes>;
  style?: InferenceStyleFragment;
  sourceImage?: FileReferenceType;
  sourceVideo?: FileReferenceType;
  sourceMesh?: ReferenceMeshType;
  mode: SessionInferenceMode;
}

export const convertFormValuesToInferenceCreationParameters = (
  options: ConvertFormValuesToInferenceCreationParametersOptions,
): InferenceParameters => {
  const { formValues, style, sourceImage, sourceVideo, sourceMesh, mode } =
    options;

  const parameters = convertFormValuesToCommonAPIParameters(formValues, mode);

  if (sourceImage) {
    parameters.files = [
      convertFileReferenceToGuidanceFileParameters(sourceImage, "INIT_IMAGE"),
      ...(parameters.files ?? []),
    ];
  }

  if (sourceVideo) {
    parameters.files = [
      convertFileReferenceToGuidanceFileParameters(sourceVideo, "INIT_VIDEO"),
      ...(parameters.files ?? []),
    ];
  }

  if (sourceMesh) {
    parameters.files = [
      formatReferenceMeshForAPI(sourceMesh),
      ...(parameters.files ?? []),
    ];
  }

  if (formValues.referenceVideos?.length) {
    parameters.files = [
      ...formValues.referenceVideos.map((video) =>
        convertFileReferenceToGuidanceFileParameters(video, "REFERENCE_VIDEO"),
      ),
      ...(parameters.files ?? []),
    ];
  }

  if (formValues.references?.length) {
    const referenceFiles = formValues.references
      .filter(
        (
          r,
        ): r is ReferenceType & {
          image: NonNullable<ReferenceType["image"]>;
        } => r.image !== null,
      )
      .map(formatReferenceForAPI);
    parameters.files = [...referenceFiles, ...(parameters.files ?? [])];
  }

  const getStaticParameters = MODE_STATIC_PARAMETERS[mode];

  return getStaticParameters
    ? { ...getStaticParameters(style), ...parameters }
    : parameters;
};
